import { type Ref, ref, onUnmounted, watch } from 'vue'

type UseFocusTrapOptions = {
  preventFocusLostOnly: boolean
}

export default function useFocusTrap(element: Ref<HTMLElement | null>, options?: UseFocusTrapOptions) {
  const isEnabled = ref(false)

  const handleFocusOut = (event: FocusEvent) => {
    const nextFocusTarget = event.relatedTarget as HTMLElement | null

    if (!isEnabled.value || element.value?.contains(nextFocusTarget)) {
      return
    }

    if (options?.preventFocusLostOnly && event.relatedTarget !== null) {
      return
    }

    element.value?.focus()
  }

  const validateInitialFocus = () => {
    if (element.value?.contains(document.activeElement)) return

    element.value?.focus()
  }

  onUnmounted(() => {
    element.value?.removeEventListener('focusout', handleFocusOut)
  })

  watch(
    isEnabled,
    (value) => {
      if (value) {
        element.value?.addEventListener('focusout', handleFocusOut)

        validateInitialFocus()
      } else {
        element.value?.removeEventListener('focusout', handleFocusOut)
      }
    },
    { immediate: true },
  )

  const enable = () => {
    isEnabled.value = true
  }

  const disable = () => {
    isEnabled.value = false
  }

  return {
    enable,
    disable,
  }
}
